import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bubbles } from "@/components/Motifs";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const redirect = new URLSearchParams(loc.search).get("redirect") || "";
  const [loading, setLoading] = useState(false);
  const [li, setLi] = useState({ email: "", password: "" });
  const [rg, setRg] = useState({ name: "", email: "", password: "", phone: "" });
  const [forgot, setForgot] = useState({ open: false, email: "", sending: false });

  const sendReset = async (e) => {
    e.preventDefault();
    if (!forgot.email) return toast.error("Please enter your email.");
    setForgot((f) => ({ ...f, sending: true }));
    try {
      await api.post("/auth/forgot-password", { email: forgot.email, origin_url: window.location.origin });
      toast.success("If an account exists for that email, a reset link is on its way. Please check your inbox.");
      setForgot({ open: false, email: "", sending: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setForgot((f) => ({ ...f, sending: false }));
    }
  };

  const afterAuth = (data) => {
    login(data.token, data.user);
    toast.success(`Welcome, ${data.user.name}!`);
    if (redirect) navigate(redirect);
    else navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", li);
      afterAuth(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...rg, origin_url: window.location.origin });
      afterAuth(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between bg-navy p-12 overflow-hidden">
        <Bubbles count={16} dark />
        <img src="/assets/mermaid1.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-navy/60" />
        <Link to="/" className="relative flex items-center gap-3 z-10">
          <img src="/assets/logo.png" alt="Float Now" className="h-16 w-16 object-contain" />
        </Link>
        <div className="relative z-10">
          <h2 className="font-serif text-4xl text-cream leading-tight">Every little swimmer<br />has an adventure.</h2>
          <p className="mt-4 text-cream/70 max-w-sm">Let's make it a safe one. Sign in to book lessons, track progress and manage your family's swim journey.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-cream">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex justify-center mb-6"><img src="/assets/logo.png" alt="Float Now" className="h-20 w-20 object-contain" /></Link>
          <div className="flex items-center gap-2 text-teal mb-2"><Waves size={20} /><span className="text-xs tracking-[0.2em] uppercase font-semibold">Float Now</span></div>
          <h1 className="font-serif text-3xl text-navy mb-6">Welcome aboard</h1>
          <Tabs defaultValue="signin">
            <TabsList className="grid grid-cols-2 w-full rounded-full bg-white border border-gold/30">
              <TabsTrigger value="signin" data-testid="tab-signin" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Create Account</TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <form onSubmit={doLogin} className="space-y-4 mt-6">
                <div><Label>Email</Label><Input type="email" required data-testid="login-email" value={li.email} onChange={(e) => setLi({ ...li, email: e.target.value })} className="mt-1 bg-white" /></div>
                <div><Label>Password</Label><Input type="password" required data-testid="login-password" value={li.password} onChange={(e) => setLi({ ...li, password: e.target.value })} className="mt-1 bg-white" /></div>
                <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-coral hover:bg-[#C95B50] text-white">{loading ? "Signing in…" : "Sign In"}</Button>
              </form>
              {!forgot.open ? (
                <button type="button" onClick={() => setForgot((f) => ({ ...f, open: true }))} data-testid="forgot-link" className="mt-4 text-sm text-teal hover:text-coral transition-colors">Forgot password?</button>
              ) : (
                <form onSubmit={sendReset} className="mt-4 rounded-2xl border border-gold/40 bg-white p-4">
                  <Label>Enter your account email</Label>
                  <div className="mt-1 flex gap-2">
                    <Input type="email" required data-testid="forgot-email" value={forgot.email} onChange={(e) => setForgot((f) => ({ ...f, email: e.target.value }))} className="bg-cream" />
                    <Button type="submit" disabled={forgot.sending} data-testid="forgot-submit" className="rounded-full bg-teal hover:bg-[#3b8b99] text-white shrink-0">{forgot.sending ? "Sending…" : "Send link"}</Button>
                  </div>
                  <button type="button" onClick={() => setForgot({ open: false, email: "", sending: false })} className="mt-2 text-xs text-navy/50">Cancel</button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4 mt-6">
                <div><Label>Full Name</Label><Input required data-testid="register-name" value={rg.name} onChange={(e) => setRg({ ...rg, name: e.target.value })} className="mt-1 bg-white" /></div>
                <div><Label>Email</Label><Input type="email" required data-testid="register-email" value={rg.email} onChange={(e) => setRg({ ...rg, email: e.target.value })} className="mt-1 bg-white" /></div>
                <div><Label>Phone (optional)</Label><Input data-testid="register-phone" value={rg.phone} onChange={(e) => setRg({ ...rg, phone: e.target.value })} className="mt-1 bg-white" /></div>
                <div><Label>Password</Label><Input type="password" required minLength={6} data-testid="register-password" value={rg.password} onChange={(e) => setRg({ ...rg, password: e.target.value })} className="mt-1 bg-white" /></div>
                <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full bg-coral hover:bg-[#C95B50] text-white">{loading ? "Creating…" : "Create Account"}</Button>
              </form>
            </TabsContent>
          </Tabs>
          <p className="text-center text-xs text-navy/50 mt-6">This website is not monitored for emergencies. In an emergency, call 911.</p>
        </div>
      </div>
    </div>
  );
}
